import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faChartLine,
    faHandshake,
    faFileContract,
    faUsers,
    faScaleBalanced,
    faGavel,
    faLightbulb,
    faShieldAlt,
    faGlobe,
    faAward,
    faCheckCircle,
    faChevronRight,
    faQuoteLeft,
    faStar,
    faBalanceScale,
    faUserTie,
    faPhone,
    faEnvelope,
    faCalendarAlt,
    faMoneyBillWave,
    faCertificate,
    faRocket,
    faUniversity,
    faIndustry,
    faFileSignature,
    faLandmark,
    faNetworkWired,
    faLock,
    faClipboardCheck,
    faBullseye
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const corporateServices = [
    {
        icon: faBuilding,
        title: "Company Incorporation",
        description: "End-to-end assistance in company registration, compliance, and governance.",
        color: "gold",
        subservices: [
            "Private/Public Company Formation",
            "LLP Registration",
            "Foreign Subsidiary Setup",
            "Compliance Management"
        ],
        stat: "500+"
    },
    {
        icon: faHandshake,
        title: "Mergers & Acquisitions",
        description: "Strategic guidance on corporate restructuring, mergers, acquisitions, and joint ventures.",
        color: "primary-blue",
        subservices: [
            "Due Diligence",
            "Transaction Structuring",
            "Share Purchase Agreements",
            "Post-Merger Integration"
        ],
        stat: "100+"
    },
    {
        icon: faFileContract,
        title: "Commercial Contracts",
        description: "Drafting, reviewing, and negotiating complex commercial agreements.",
        color: "gold",
        subservices: [
            "Service Agreements",
            "Distribution Agreements",
            "Franchise Agreements",
            "Technology Contracts"
        ],
        stat: "1000+"
    },
    {
        icon: faChartLine,
        title: "Corporate Governance",
        description: "Advisory on board matters, compliance, and corporate governance frameworks.",
        color: "primary-blue",
        subservices: [
            "Board Advisory",
            "SEBI Compliance",
            "Corporate Policies",
            "ESG Compliance"
        ],
        stat: "200+"
    },
    {
        icon: faShieldAlt,
        title: "Compliance & Regulatory",
        description: "Ensuring compliance with company law, FEMA, RBI, and other regulations.",
        color: "gold",
        subservices: [
            "Companies Act Compliance",
            "FEMA Advisory",
            "RBI Regulations",
            "Corporate Filings"
        ],
        stat: "300+"
    },
    {
        icon: faGlobe,
        title: "International Business",
        description: "Cross-border transactions, foreign investments, and international contracts.",
        color: "primary-blue",
        subservices: [
            "Foreign Direct Investment",
            "Joint Ventures Abroad",
            "Cross-border M&A",
            "International Contracts"
        ],
        stat: "150+"
    }
];

const commercialServices = [
    {
        area: "Contract Law",
        icon: faFileSignature,
        description: "Drafting and enforcement of commercial contracts and agreements.",
        matters: [
            "Supply Contracts",
            "Agency Agreements",
            "Licensing Agreements",
            "Service Level Agreements"
        ],
        color: "gold"
    },
    {
        area: "Competition Law",
        icon: faScaleBalanced,
        description: "Compliance with competition regulations and antitrust matters.",
        matters: [
            "CCI Filings",
            "Anti-competitive Practices",
            "Merger Control",
            "Competition Compliance"
        ],
        color: "primary-blue"
    },
    {
        area: "Intellectual Property",
        icon: faLightbulb,
        description: "Protection and enforcement of intellectual property rights.",
        matters: [
            "Trademark Registration",
            "Patent Filing",
            "Copyright Protection",
            "IP Licensing"
        ],
        color: "gold"
    },
    {
        area: "Banking & Finance",
        icon: faMoneyBillWave,
        description: "Legal aspects of banking transactions and financial agreements.",
        matters: [
            "Loan Documentation",
            "Security Creation",
            "Project Finance",
            "Financial Regulations"
        ],
        color: "primary-blue"
    }
];

const sectors = [
    {
        sector: "Technology & IT",
        icon: faNetworkWired,
        description: "Legal services for tech companies, startups, and IT enterprises.",
        cases: ["SaaS Agreements", "Data Protection", "Software Licensing", "Tech M&A"],
        clients: ["PhonePe", "Tech Startups", "IT Services Companies"],
        color: "gold"
    },
    {
        sector: "Manufacturing",
        icon: faIndustry,
        description: "Legal support for manufacturing and industrial enterprises.",
        cases: ["Supply Chain Contracts", "Factory Agreements", "Export Compliance", "Labor Matters"],
        clients: ["Hero MotoCorp", "Manufacturing Units", "Industrial Groups"],
        color: "primary-blue"
    },
    {
        sector: "Infrastructure",
        icon: faBuilding,
        description: "Legal advisory for infrastructure and construction companies.",
        cases: ["EPC Contracts", "Project Finance", "PPP Agreements", "Infrastructure Disputes"],
        clients: ["Tata Projects", "HCC", "Construction Giants"],
        color: "gold"
    },
    {
        sector: "Banking & Financial Services",
        icon: faLandmark,
        description: "Specialized legal services for banks and financial institutions.",
        cases: ["Regulatory Compliance", "Loan Recovery", "Securitization", "Banking Operations"],
        clients: ["HDFC Bank", "Financial Institutions", "NBFCs"],
        color: "primary-blue"
    }
];

const legalFrameworks = [
    {
        framework: "Companies Act, 2013",
        icon: faClipboardCheck,
        description: "Comprehensive legislation governing company formation, management, and operations.",
        relevance: "Corporate Governance & Compliance"
    },
    {
        framework: "FEMA Regulations",
        icon: faGlobe,
        description: "Foreign Exchange Management Act governing cross-border transactions and investments.",
        relevance: "International Business & FDI"
    },
    {
        framework: "SEBI Regulations",
        icon: faChartLine,
        description: "Securities and Exchange Board of India regulations for capital markets.",
        relevance: "Capital Markets & Listed Companies"
    },
    {
        framework: "Competition Act, 2002",
        icon: faScaleBalanced,
        description: "Legislation promoting fair competition and preventing anti-competitive practices.",
        relevance: "M&A & Market Competition"
    },
    {
        framework: "Contract Act, 1872",
        icon: faFileContract,
        description: "Fundamental law governing contracts and commercial agreements.",
        relevance: "Commercial Contracts & Agreements"
    },
    {
        framework: "Insolvency & Bankruptcy Code",
        icon: faGavel,
        description: "Framework for insolvency resolution and bankruptcy proceedings.",
        relevance: "Corporate Restructuring & Recovery"
    }
];

const caseStudies = [
    {
        title: "Cross-border M&A Deal",
        client: "Indian Tech Company",
        deal: "Acquisition of European Software Firm",
        value: "$50 Million",
        duration: "6 months",
        result: "Successful Acquisition",
        icon: faGlobe
    },
    {
        title: "Corporate Restructuring",
        client: "Family-owned Business",
        deal: "Conversion to Professional Management",
        value: "₹200 Crore Group",
        duration: "4 months",
        result: "Smooth Transition",
        icon: faBuilding
    },
    {
        title: "Joint Venture Formation",
        client: "Indian- Japanese Partnership",
        deal: "Automotive Components JV",
        value: "₹150 Crore Investment",
        duration: "3 months",
        result: "Successful Launch",
        icon: faHandshake
    },
    {
        title: "Regulatory Compliance",
        client: "Listed Company",
        deal: "SEBI Compliance Overhaul",
        value: "Market Cap: ₹5000 Crore",
        duration: "8 months",
        result: "Full Compliance",
        icon: faShieldAlt
    }
];

const whyChooseUs = [
    {
        reason: "London-Educated Expertise",
        description: "International legal training from LSE and UCL",
        icon: faUniversity,
        color: "gold"
    },
    {
        reason: "Corporate Experience",
        description: "Experience with leading law firms and corporations",
        icon: faBuilding,
        color: "primary-blue"
    },
    {
        reason: "Strategic Approach",
        description: "Business-focused legal solutions",
        icon: faBullseye,
        color: "gold"
    },
    {
        reason: "Client-Centric",
        description: "Personalized attention to corporate clients",
        icon: faUsers,
        color: "primary-blue"
    }
];

export const CorporateCommercialLaw = () => {
    const [activeService, setActiveService] = useState(0);
    const [hoveredSector, setHoveredSector] = useState(null);

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
                            style={{
                                color: "var(--gold)",
                                fontWeight: "600",
                                letterSpacing: "2px",
                                fontSize: "0.9rem",
                                textTransform: "uppercase",
                                border: "1px solid var(--gold-light)",
                                background: "rgba(184, 134, 11, 0.05)",
                                backdropFilter: "blur(4px)"
                            }}
                        >
                            Corporate Legal Excellence
                        </span>


                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Corporate Legal Excellence Commercial Law Services
                        </motion.h4>

                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Comprehensive legal solutions for corporations, businesses, and commercial
                        enterprises. Our London-educated Advocates-on-Record provide strategic
                        counsel for corporate growth and compliance.
                    </motion.p>
                </motion.div>

                {/* Corporate Services */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faBuilding} className="me-3" style={{ color: "var(--gold)" }} />
                            Corporate Legal Services
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            End-to-end legal support for corporate entities and businesses
                        </p>
                    </div>

                    <div className="row g-4">
                        {corporateServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                                onMouseEnter={() => setActiveService(index)}
                            >
                                <motion.div
                                    className="h-100 p-4 rounded-4 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="position-relative z-1 h-100 d-flex flex-column">
                                        {/* Icon & Stat */}
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <motion.div
                                                className="p-3 rounded-3"
                                                style={{
                                                    backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                    color: `var(--${service.color})`
                                                }}
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6, ease: "easeInOut" }}
                                            >
                                                <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                            </motion.div>

                                            {/* <motion.div
                                                className="display-6 fw-bold"
                                                style={{
                                                    color: `var(--${service.color})`,
                                                    fontFamily: "'Playfair Display', serif"
                                                }}
                                                animate={{
                                                    scale: activeService === index ? 1.1 : 1
                                                }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                {service.stat}
                                            </motion.div> */}
                                        </div>

                                        {/* Title & Description */}
                                        <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            {service.title}
                                        </h5>

                                        <p className="mb-4 flex-grow-1 small"
                                            style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {service.description}
                                        </p>

                                        {/* Subservices */}
                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: `var(--${service.color}-light)` }}
                                        >
                                            <div className="vstack gap-2">
                                                {service.subservices.map((subservice, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2"
                                                        initial={{ opacity: 0, x: -10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faChevronRight}
                                                            style={{
                                                                color: `var(--${service.color})`,
                                                                fontSize: "0.7rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {subservice}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Commercial Law Areas */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faScaleBalanced} className="me-3" style={{ color: "var(--gold)" }} />
                            Commercial Law Expertise
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Specialized legal services for commercial transactions and agreements
                        </p>
                    </div>

                    <div className="row g-4">
                        {commercialServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${service.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${service.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${service.color}-rgb), 0.1)`,
                                                color: `var(--${service.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={service.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {service.area}
                                    </h5>

                                    <p className="small text-center mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {service.description}
                                    </p>

                                    <div className="vstack gap-2">
                                        {service.matters.map((matter, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    style={{
                                                        color: `var(--${service.color})`,
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {matter}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Sector Expertise */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faIndustry} className="me-3" style={{ color: "var(--gold)" }} />
                            Industry-Specific Expertise
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Legal services tailored to specific industries and sectors
                        </p>
                    </div>

                    <div className="row g-4">
                        {sectors.map((sector, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setHoveredSector(index)}
                                onMouseLeave={() => setHoveredSector(null)}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${sector.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${sector.color})`
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${sector.color}-rgb), 0.1)`,
                                                color: `var(--${sector.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={sector.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h5 className="fw-bold text-center mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {sector.sector}
                                    </h5>

                                    <p className="small text-center mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6
                                    }}>
                                        {sector.description}
                                    </p>

                                    <div className="vstack gap-2 mb-4">
                                        {sector.cases.map((caseType, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={faChevronRight}
                                                    style={{
                                                        color: `var(--${sector.color})`,
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {caseType}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="pt-3 border-top"
                                        style={{ borderColor: `var(--${sector.color}-light)` }}
                                    >
                                        <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                            Representative Clients:
                                        </div>
                                        <div className="small" style={{ color: "var(--gold)" }}>
                                            {sector.clients.join(", ")}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Legal Frameworks */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faGavel} className="me-3" style={{ color: "var(--gold)" }} />
                            Key Legal Frameworks
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Expertise in major corporate and commercial legislation
                        </p>
                    </div>

                    <div className="row g-4">
                        {legalFrameworks.map((framework, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)"
                                    }}
                                >
                                    <div className="d-flex align-items-start gap-3 mb-3">
                                        <div className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                color: "var(--gold)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={framework.icon} />
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                                {framework.framework}
                                            </h6>
                                            <div className="small" style={{ color: "var(--text-primary)", lineHeight: 1.5 }}>
                                                {framework.description}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-3 pt-3 border-top"
                                        style={{ borderColor: "var(--gold-light)" }}
                                    >
                                        <span className="badge px-3 py-1"
                                            style={{
                                                backgroundColor: "rgba(var(--primary-blue-rgb), 0.1)",
                                                color: "var(--primary-blue)",
                                                fontSize: "0.75rem"
                                            }}
                                        >
                                            {framework.relevance}
                                        </span>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Case Studies */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faAward} className="me-3" style={{ color: "var(--gold)" }} />
                            Corporate Case Studies
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Successful corporate transactions and legal solutions
                        </p>
                    </div>

                    <div className="row g-4">
                        {caseStudies.map((caseStudy, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: "var(--gold)"
                                    }}
                                >
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <div className="p-2 rounded-3 flex-shrink-0"
                                                style={{
                                                    backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                    color: "var(--gold)"
                                                }}
                                            >
                                                <FontAwesomeIcon icon={caseStudy.icon} />
                                            </div>
                                            <h6 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                                {caseStudy.title}
                                            </h6>
                                        </div>
                                        <div className="small mb-3" style={{ color: "var(--gold)" }}>
                                            Client: {caseStudy.client}
                                        </div>
                                    </div>

                                    <div className="vstack gap-3 mb-4">
                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Transaction
                                            </div>
                                            <div className="small" style={{ color: "var(--text-primary)" }}>
                                                {caseStudy.deal}
                                            </div>
                                        </div>

                                        <div className="row g-2">
                                            <div className="col-6">
                                                <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                    Value
                                                </div>
                                                <div className="small fw-bold" style={{ color: "var(--gold)" }}>
                                                    {caseStudy.value}
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                    Duration
                                                </div>
                                                <div className="small" style={{ color: "var(--text-primary)" }}>
                                                    {caseStudy.duration}
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="small fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                Result
                                            </div>
                                            <div className="small fw-bold" style={{ color: "var(--green-accent)" }}>
                                                {caseStudy.result}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Why Choose RNA */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        <FontAwesomeIcon icon={faUserTie} className="me-3" style={{ color: "var(--gold)" }} />
                                        Why Choose RNA for Corporate Law?
                                    </h3>

                                    <div className="row g-4 mb-4">
                                        {whyChooseUs.map((reason, index) => (
                                            <div key={index} className="col-md-6">
                                                <div className="d-flex align-items-start gap-3 mb-3">
                                                    <div className="p-2 rounded-3 flex-shrink-0"
                                                        style={{
                                                            backgroundColor: `rgba(var(--${reason.color}-rgb), 0.2)`,
                                                            color: `var(--${reason.color})`
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={reason.icon} />
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                            {reason.reason}
                                                        </div>
                                                        <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                            {reason.description}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* <div className="row g-4">
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    500+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Companies Incorporated
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    100+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    M&A Transactions
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-center">
                                                <div className="display-4 fw-bold mb-1" style={{ color: "var(--gold)" }}>
                                                    1000+
                                                </div>
                                                <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                    Contracts Drafted
                                                </div>
                                            </div>
                                        </div>
                                    </div> */}
                                </motion.div>
                            </div>

                            <div className="col-lg-4">
                                <motion.div
                                    className="text-center"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <div className="p-4 rounded-4 d-inline-block"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.15)",
                                            border: "2px solid var(--gold)",
                                            backdropFilter: "blur(10px)"
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faBuilding}
                                            className="mb-3"
                                            style={{ fontSize: "3rem", color: "var(--gold)" }}
                                        />
                                        <div className="fw-bold mb-2" style={{ color: "var(--white)", fontSize: "1.2rem" }}>
                                            Corporate Legal Excellence
                                        </div>
                                        <div style={{ color: "var(--gold)" }}>
                                            Strategic Business Solutions
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* CTA Section */}
          
                <Cta Title="Need Corporate Legal Expertise?" description=" Contact our London-educated corporate law specialists for strategic
                            legal solutions that drive business growth and ensure compliance."/>
            </div>
        </div>
    );
};